import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { 
  ArrowLeft,
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  Maximize, 
  Minimize,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-blue-50 via-indigo-50 to-slate-50 border border-blue-200 rounded-lg p-4 my-4 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-indigo-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-1">
          <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-3 w-3 text-white" />
          </div>
        </div>
        <div className="ml-3">
          <h4 className="text-sm font-bold text-slate-900 mb-1 tracking-tight">Key Takeaway</h4>
          <p className="text-slate-700 leading-relaxed text-xs">{children}</p>
        </div>
      </div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener and keyboard navigation
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFullscreen) {
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          nextSlide();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          prevSlide();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          toggleFullscreen();
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef} 
      className={`w-full bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden mb-8 transition-all duration-300 ${
        isFullscreen 
          ? 'fixed inset-0 z-50 bg-white overflow-y-auto max-w-none' 
          : 'max-w-none'
      }`}
    >
      <div className={`p-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-slate-50 flex justify-between items-center ${
        isFullscreen ? 'sticky top-0 z-10' : ''
      }`}>
        <h3 className="text-base font-medium text-slate-800">SHIFT Framework Lecture</h3>
        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-500 font-medium">
            Slide {currentSlide + 1} of {slides.length}
          </div>
          <Button 
            onClick={toggleFullscreen}
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            {isFullscreen ? (
              <>
                <Minimize className="h-4 w-4" />
                Exit Fullscreen
              </>
            ) : (
              <>
                <Maximize className="h-4 w-4" />
                Fullscreen
              </>
            )}
          </Button>
        </div>
      </div>
      
      <div className={`${
        isFullscreen 
          ? 'p-8 md:p-12 lg:p-16 max-w-7xl mx-auto' 
          : 'p-6 md:p-8 lg:p-10'
      }`}>
        <h2 className={`font-semibold text-slate-900 mb-6 tracking-tight leading-tight ${
          isFullscreen ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'
        }`}>
          {slides[currentSlide].title}
        </h2>
        <div className={`mb-6 overflow-y-auto pr-2 thin-scrollbar ${
          isFullscreen 
            ? 'min-h-[60vh] max-h-[70vh] text-base' 
            : 'min-h-[500px] max-h-[600px] text-sm'
        }`}>
          {slides[currentSlide].content}
        </div>
      </div>
      
      <div className={`px-6 md:px-8 lg:px-10 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between ${
        isFullscreen ? 'sticky bottom-0 z-10' : ''
      }`}>
        <button 
          onClick={prevSlide}
          className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
        >
          <ChevronLeft className="h-5 w-5 mr-1.5" />
          Previous
        </button>
        
        <div className="flex space-x-2.5">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={index === currentSlide 
                ? "h-2.5 w-2.5 rounded-full bg-blue-600 shadow-sm" 
                : "h-2.5 w-2.5 rounded-full bg-slate-300 hover:bg-slate-400 transition-colors"}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <button 
          onClick={nextSlide}
          className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
        >
          Next
          <ChevronRight className="h-5 w-5 ml-1.5" />
        </button>
      </div>
    </div>
  );
};

// SVG Diagram components for the lecture slides
const TitleDiagram = () => (
  <svg viewBox="0 0 600 280" className="w-full h-auto max-w-xl mx-auto my-8">
    <defs>
      <linearGradient id="bg-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e3f2fd" />
        <stop offset="100%" stopColor="#bbdefb" />
      </linearGradient>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="3" stdDeviation="5" floodOpacity="0.1"/>
      </filter>
    </defs>
    <rect width="600" height="280" fill="url(#bg-gradient)" rx="12" ry="12" filter="url(#shadow)" />
    
    {/* Abstract cellular network visualization */}
    <g transform="translate(300, 140)">
      {/* Central Node */}
      <circle cx="0" cy="0" r="24" fill="#1E88E5" opacity="0.9">
        <animate attributeName="opacity" values="0.8;0.95;0.8" dur="3s" repeatCount="indefinite" />
      </circle>
      
      {/* Connected Nodes - First layer */}
      <circle cx="-90" cy="-70" r="18" fill="#1565C0" opacity="0.8" />
      <circle cx="90" cy="-70" r="18" fill="#1565C0" opacity="0.8" />
      <circle cx="-90" cy="70" r="18" fill="#1565C0" opacity="0.8" />
      <circle cx="90" cy="70" r="18" fill="#1565C0" opacity="0.8" />
      <circle cx="-140" cy="0" r="18" fill="#1565C0" opacity="0.8" />
      <circle cx="140" cy="0" r="18" fill="#1565C0" opacity="0.8" />
      
      {/* Connection lines */}
      <line x1="0" y1="0" x2="-90" y2="-70" stroke="#90CAF9" strokeWidth="2.5" />
      <line x1="0" y1="0" x2="90" y2="-70" stroke="#90CAF9" strokeWidth="2.5" />
      <line x1="0" y1="0" x2="-90" y2="70" stroke="#90CAF9" strokeWidth="2.5" />
      <line x1="0" y1="0" x2="90" y2="70" stroke="#90CAF9" strokeWidth="2.5" />
      <line x1="0" y1="0" x2="-140" y2="0" stroke="#90CAF9" strokeWidth="2.5" />
      <line x1="0" y1="0" x2="140" y2="0" stroke="#90CAF9" strokeWidth="2.5" />
      
      {/* Secondary connections */}
      <line x1="-90" y1="-70" x2="-140" y2="0" stroke="#90CAF9" strokeWidth="1.5" opacity="0.7" />
      <line x1="90" y1="-70" x2="140" y2="0" stroke="#90CAF9" strokeWidth="1.5" opacity="0.7" />
      <line x1="-90" y1="70" x2="-140" y2="0" stroke="#90CAF9" strokeWidth="1.5" opacity="0.7" />
      <line x1="90" y1="70" x2="140" y2="0" stroke="#90CAF9" strokeWidth="1.5" opacity="0.7" />
    </g>
    
    {/* Label at the bottom */}
    <text x="300" y="250" fontFamily="sans-serif" fontSize="14" fontWeight="500" fill="#1565C0" textAnchor="middle">
      Cellular Network Interactions in the SHIFT Framework
    </text>
  </svg>
);

const HealthspanDiagram = () => (
  <svg viewBox="0 0 600 320" className="w-full h-auto max-w-xl mx-auto my-8">
    <defs>
      <linearGradient id="bar-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#2196F3" />
        <stop offset="100%" stopColor="#42A5F5" />
      </linearGradient>
      <filter id="shadow-bars" x="-5%" y="-5%" width="110%" height="110%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
      <linearGradient id="diagram-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="100%" stopColor="#f1f5f9" />
      </linearGradient>
    </defs>
    
    {/* Background */}
    <rect width="600" height="320" fill="url(#diagram-bg)" rx="8" ry="8" filter="url(#shadow-bars)" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="20" fontWeight="bold" fill="#334155">
      Lifespan vs. Healthspan
    </text>
    
    {/* X-Axis Label */}
    <text x="300" y="290" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="500" fill="#64748b">
      Goals of Intervention
    </text>
    
    {/* Labels for bar groups */}
    <text x="180" y="260" textAnchor="middle" fontFamily="sans-serif" fontSize="15" fontWeight="500" fill="#475569">
      Average Individual
    </text>
    <text x="420" y="260" textAnchor="middle" fontFamily="sans-serif" fontSize="15" fontWeight="500" fill="#475569">
      Longevity Medicine Goal
    </text>
    
    {/* Average Individual Bars */}
    <rect x="120" y="90" width="120" height="40" rx="3" ry="3" fill="#e2e8f0" /> {/* Lifespan bar - gray */}
    <rect x="120" y="90" width="70" height="40" rx="3" ry="3" fill="url(#bar-gradient)" filter="url(#shadow-bars)" /> {/* Healthspan bar - blue */}
    
    {/* Goal Bars */}
    <rect x="360" y="90" width="140" height="40" rx="3" ry="3" fill="#e2e8f0" /> {/* Lifespan bar - slightly longer */}
    <rect x="360" y="90" width="130" height="40" rx="3" ry="3" fill="url(#bar-gradient)" filter="url(#shadow-bars)" /> {/* Healthspan bar - almost matching lifespan */}
    
    {/* Arrow showing extension */}
    <line x1="190" y1="170" x2="490" y2="170" stroke="#4CAF50" strokeWidth="2.5" strokeDasharray="6,4" />
    <polygon points="490,170 480,165 480,175" fill="#4CAF50" />
    <text x="340" y="190" textAnchor="middle" fontFamily="sans-serif" fontSize="16" fontWeight="500" fill="#4CAF50">
      Extending Healthspan
    </text>
    
    {/* Legend */}
    <rect x="420" y="220" width="140" height="30" rx="15" fill="white" fillOpacity="0.8" />
    <text x="470" y="240" textAnchor="middle" fontFamily="sans-serif" fontSize="15" fontWeight="bold" fill="#e53935">
      The Healthspan Gap
    </text>
    
    {/* Legend with improved styling */}
    <rect x="150" y="330" width="300" height="36" rx="6" fill="white" fillOpacity="0.9" stroke="#e2e8f0" strokeWidth="1" />
    <line x1="170" y1="348" x2="210" y2="348" stroke="#64748b" strokeWidth="3" />
    <text x="220" y="352" textAnchor="start" fontFamily="sans-serif" fontSize="13" fontWeight="500" fill="#475569">Lifespan</text>
    
    <line x1="290" y1="348" x2="330" y2="348" stroke="#2196F3" strokeWidth="3" />
    <text x="340" y="352" textAnchor="start" fontFamily="sans-serif" fontSize="13" fontWeight="500" fill="#475569">Healthspan</text>
  </svg>
);

const HealthspanGapDiagram = () => (
  <svg viewBox="0 0 600 380" className="w-full h-auto max-w-xl mx-auto my-8">
    <defs>
      <linearGradient id="gap-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#ef5350" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#ef5350" stopOpacity="0.15" />
      </linearGradient>
      <filter id="graph-shadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.08"/>
      </filter>
      <linearGradient id="graph-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fafafa" />
        <stop offset="100%" stopColor="#f5f7fa" />
      </linearGradient>
    </defs>
    
    {/* Background */}
    <rect width="600" height="380" fill="url(#graph-bg)" rx="8" ry="8" filter="url(#graph-shadow)" />
    
    {/* Title */}
    <text x="300" y="45" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="bold" fill="#334155">
      The Healthspan Gap
    </text>
    
    {/* X & Y axis */}
    <line x1="100" y1="70" x2="100" y2="270" stroke="#cbd5e1" strokeWidth="2" /> {/* Y-axis */}
    <line x1="100" y1="270" x2="520" y2="270" stroke="#cbd5e1" strokeWidth="2" /> {/* X-axis */}
    
    {/* Axis Labels */}
    <text x="310" y="310" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="15" fontWeight="500" fill="#64748b">
      Time (Years)
    </text>
    <text x="50" y="170" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="15" fontWeight="500" fill="#64748b" transform="rotate(-90, 50, 170)">
      Health / Function
    </text>
    
    {/* X-axis ticks */}
    <line x1="100" y1="270" x2="100" y2="275" stroke="#64748b" strokeWidth="1.5" />
    <text x="100" y="290" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="500" fill="#64748b">0</text>
    
    <line x1="200" y1="270" x2="200" y2="275" stroke="#64748b" strokeWidth="1.5" />
    <text x="200" y="290" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="500" fill="#64748b">25</text>
    
    <line x1="300" y1="270" x2="300" y2="275" stroke="#64748b" strokeWidth="1.5" />
    <text x="300" y="290" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="500" fill="#64748b">50</text>
    
    <line x1="400" y1="270" x2="400" y2="275" stroke="#64748b" strokeWidth="1.5" />
    <text x="400" y="290" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="500" fill="#64748b">75</text>
    
    <line x1="500" y1="270" x2="500" y2="275" stroke="#64748b" strokeWidth="1.5" />
    <text x="500" y="290" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="500" fill="#64748b">100</text>
    
    {/* Grid lines (subtle) */}
    <line x1="100" y1="170" x2="520" y2="170" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4,4" />
    <line x1="300" y1="70" x2="300" y2="270" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4,4" />
    
    {/* Lifespan Line (gray) */}
    <path d="M100,250 C200,240 300,200 400,130 C450,100 480,70 520,60" stroke="#64748b" strokeWidth="3.5" fill="none" />
    
    {/* Healthspan Line (blue) */}
    <path d="M100,250 C200,240 300,200 375,200 C425,200 460,210 520,210" stroke="#2196F3" strokeWidth="3.5" fill="none" />
    
    {/* Healthspan Gap (shaded area) */}
    <path d="M375,200 C425,200 460,210 520,210 L520,60 C480,70 450,100 400,130 C380,155 375,200 375,200 Z" fill="url(#gap-gradient)" />
    
    {/* Gap Label with improved visibility */}
    <rect x="400" y="120" width="140" height="30" rx="15" fill="white" fillOpacity="0.8" />
    <text x="470" y="140" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="15" fontWeight="bold" fill="#e53935">
      The Healthspan Gap
    </text>
    
    {/* Legend with improved styling */}
    <rect x="150" y="330" width="300" height="36" rx="6" fill="white" fillOpacity="0.9" stroke="#e2e8f0" strokeWidth="1" />
    <line x1="170" y1="348" x2="210" y2="348" stroke="#64748b" strokeWidth="3" />
    <text x="220" y="352" textAnchor="start" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="500" fill="#475569">Lifespan</text>
    
    <line x1="290" y1="348" x2="330" y2="348" stroke="#2196F3" strokeWidth="3" />
    <text x="340" y="352" textAnchor="start" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="500" fill="#475569">Healthspan</text>
  </svg>
);

const CellularPerspectiveDiagram = () => (
  <svg viewBox="0 0 600 380" className="w-full h-auto max-w-xl mx-auto my-8">
    <defs>
      <linearGradient id="cell-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="100%" stopColor="#f1f5f9" />
      </linearGradient>
      <filter id="cell-shadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
      <radialGradient id="nucleus-gradient" cx="50%" cy="50%" r="50%" fx="40%" fy="40%">
        <stop offset="0%" stopColor="#bbdefb" />
        <stop offset="100%" stopColor="#90caf9" />
      </radialGradient>
      <radialGradient id="mito-gradient" cx="50%" cy="50%" r="60%" fx="35%" fy="35%">
        <stop offset="0%" stopColor="#b3e5fc" />
        <stop offset="100%" stopColor="#4fc3f7" />
      </radialGradient>
    </defs>
    
    {/* Background */}
    <rect width="600" height="380" fill="url(#cell-bg)" rx="8" ry="8" filter="url(#cell-shadow)" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="20" fontWeight="bold" fill="#334155">
      Aging at the Cellular Level
    </text>
    
    {/* Cell Outline - Main Cell */}
    <ellipse cx="300" cy="190" rx="170" ry="130" fill="#e3f2fd" stroke="#90CAF9" strokeWidth="2.5" filter="url(#cell-shadow)" />
    
    {/* Nucleus */}
    <circle cx="280" cy="190" r="50" fill="url(#nucleus-gradient)" stroke="#64B5F6" strokeWidth="2" />
    <circle cx="280" cy="190" r="40" fill="none" stroke="#64B5F6" strokeWidth="1.5" strokeDasharray="4,2" />
    
    {/* DNA in Nucleus */}
    <path d="M260,175 C270,165 290,165 300,175 C310,185 290,195 280,205 C270,215 290,225 300,215" 
      fill="none" stroke="#2196F3" strokeWidth="1.5" strokeDasharray="1,1" opacity="0.8" />
    
    {/* Mitochondria */}
    <ellipse cx="200" cy="140" rx="35" ry="18" fill="url(#mito-gradient)" stroke="#29B6F6" strokeWidth="2" />
    <ellipse cx="200" cy="140" rx="28" ry="12" fill="none" stroke="#29B6F6" strokeWidth="1.5" strokeDasharray="2,1" />
    
    <ellipse cx="370" cy="150" rx="30" ry="16" fill="url(#mito-gradient)" stroke="#29B6F6" strokeWidth="2" />
    <ellipse cx="370" cy="150" rx="24" ry="10" fill="none" stroke="#29B6F6" strokeWidth="1.5" strokeDasharray="2,1" />
    
    {/* Endoplasmic Reticulum */}
    <path d="M350,230 C380,240 390,220 410,230 C430,240 440,220 450,225" fill="none" stroke="#4FC3F7" strokeWidth="2.5" />
    <path d="M350,240 C380,250 395,230 410,240 C425,250 440,230 450,235" fill="none" stroke="#4FC3F7" strokeWidth="2.5" />
    
    {/* Connecting Nodes - Pathways */}
    <circle cx="240" cy="160" r="14" fill="#1565C0" opacity="0.8">
      <animate attributeName="opacity" values="0.6;0.9;0.6" dur="3s" repeatCount="indefinite" />
    </circle>
    <circle cx="330" cy="170" r="14" fill="#1565C0" opacity="0.8">
      <animate attributeName="opacity" values="0.6;0.9;0.6" dur="4s" repeatCount="indefinite" />
    </circle>
    <circle cx="270" cy="240" r="14" fill="#1565C0" opacity="0.8">
      <animate attributeName="opacity" values="0.6;0.9;0.6" dur="3.5s" repeatCount="indefinite" />
    </circle>
    <circle cx="340" cy="230" r="14" fill="#1565C0" opacity="0.8">
      <animate attributeName="opacity" values="0.6;0.9;0.6" dur="4.5s" repeatCount="indefinite" />
    </circle>
    
    {/* Connection lines */}
    <line x1="240" y1="160" x2="330" y2="170" stroke="#90CAF9" strokeWidth="2" />
    <line x1="330" y1="170" x2="340" y2="230" stroke="#90CAF9" strokeWidth="2" />
    <line x1="340" y1="230" x2="270" y2="240" stroke="#90CAF9" strokeWidth="2" />
    <line x1="270" y1="240" x2="240" y2="160" stroke="#90CAF9" strokeWidth="2" />
    <line x1="240" y1="160" x2="280" y2="190" stroke="#90CAF9" strokeWidth="2" />
    <line x1="330" y1="170" x2="280" y2="190" stroke="#90CAF9" strokeWidth="2" />
    
    {/* Damaged or Stressed Components - faded, irregular shapes */}
    <path d="M200,220 C210,225 220,215 230,222 C240,230 235,240 222,235 C210,230 190,215 200,220" fill="#ef9a9a" stroke="#e57373" strokeWidth="1.5" opacity="0.8" />
    <path d="M350,110 C360,105 370,115 365,122 C360,128 340,120 350,110" fill="#ef9a9a" stroke="#e57373" strokeWidth="1.5" opacity="0.8" />
    
    {/* Labels */}
    <text x="280" y="190" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fontWeight="bold" fill="#0d47a1">
      Nucleus
    </text>
    <text x="200" y="140" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="9" fontWeight="bold" fill="#01579b">
      Mitochondria
    </text>
    <text x="340" y="280" textAnchor="start" fontFamily="system-ui, sans-serif" fontSize="11" fontWeight="medium" fill="#0277bd">
      <tspan x="410" y="245">Endoplasmic</tspan>
      <tspan x="410" y="260">Reticulum</tspan>
    </text>
    <text x="200" y="240" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="10" fontWeight="medium" fill="#c62828">
      Damaged
    </text>
    
    {/* Legend Box */}
    <rect x="150" y="320" width="300" height="40" rx="5" fill="white" fillOpacity="0.9" stroke="#e2e8f0" strokeWidth="1" />
    <circle cx="170" y="340" r="8" fill="#1565C0" opacity="0.8" />
    <text x="185" y="344" textAnchor="start" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="500" fill="#475569">Signaling Nodes</text>
    
    <circle cx="290" y="340" r="8" fill="#ef9a9a" stroke="#e57373" strokeWidth="1" />
    <text x="305" y="344" textAnchor="start" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="500" fill="#475569">Cellular Damage</text>
    
    {/* Additional References */}
    <text x="160" y="85" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="500" fill="#334155">
      Intracellular Signaling Network
    </text>
    <text x="440" y="85" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="500" fill="#334155">
      SHIFT Framework Components at Cellular Level
    </text>

  </svg>
);

const ShiftFrameworkDiagram = () => (
  <svg viewBox="0 0 600 400" className="w-full h-auto max-w-md mx-auto my-6">
    {/* Background */}
    <rect width="600" height="400" fill="#f5f7fa" rx="5" ry="5" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="20" fontWeight="bold" fill="#333">
      The SHIFT Framework
    </text>
    
    {/* Central Pentagon */}
    <polygon points="300,100 370,150 340,230 260,230 230,150" 
             fill="#bbdefb" stroke="#1976D2" strokeWidth="2" />
    <text x="300" y="175" textAnchor="middle" fontFamily="sans-serif" fontSize="16" fontWeight="bold" fill="#0D47A1">
      Healthy
    </text>
    <text x="300" y="195" textAnchor="middle" fontFamily="sans-serif" fontSize="16" fontWeight="bold" fill="#0D47A1">
      Aging
    </text>
    
    {/* S - Sensing Box */}
    <rect x="100" y="70" width="160" height="60" rx="5" ry="5" fill="#e8f5e9" stroke="#43A047" strokeWidth="2" />
    <text x="180" y="95" textAnchor="middle" fontFamily="sans-serif" fontSize="16" fontWeight="bold" fill="#2E7D32">
      S
    </text>
    <text x="180" y="115" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#2E7D32">
      Sensing & Metabolic
    </text>
    <text x="180" y="135" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#2E7D32">
      Fuel-Gauging
    </text>
    <line x1="230" y1="100" x2="270" y2="130" stroke="#43A047" strokeWidth="2" />
    
    {/* H - Homeostatic Box */}
    <rect x="400" y="70" width="160" height="60" rx="5" ry="5" fill="#e3f2fd" stroke="#1976D2" strokeWidth="2" />
    <text x="480" y="95" textAnchor="middle" fontFamily="sans-serif" fontSize="16" fontWeight="bold" fill="#1565C0">
      H
    </text>
    <text x="480" y="115" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#1565C0">
      Homeostatic
    </text>
    <text x="480" y="135" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#1565C0">
      Defence & Repair
    </text>
    <line x1="400" y1="100" x2="345" y2="130" stroke="#1976D2" strokeWidth="2" />
    
    {/* I - Inflammation Box */}
    <rect x="440" y="200" width="160" height="60" rx="5" ry="5" fill="#fff3e0" stroke="#F57C00" strokeWidth="2" />
    <text x="520" y="225" textAnchor="middle" fontFamily="sans-serif" fontSize="16" fontWeight="bold" fill="#E65100">
      I
    </text>
    <text x="520" y="245" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#E65100">
      Inflammation &
    </text>
    <text x="520" y="265" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#E65100">
      Immune Tone
    </text>
    <line x1="440" y1="230" x2="340" y2="220" stroke="#F57C00" strokeWidth="2" />
    
    {/* F - Fibrosis Box */}
    <rect x="220" y="270" width="160" height="60" rx="5" ry="5" fill="#e1f5fe" stroke="#0288D1" strokeWidth="2" />
    <text x="300" y="295" textAnchor="middle" fontFamily="sans-serif" fontSize="16" fontWeight="bold" fill="#01579B">
      F
    </text>
    <text x="300" y="315" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#01579B">
      Fibrosis / Structural
    </text>
    <text x="300" y="335" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#01579B">
      Remodeling
    </text>
    <line x1="300" y1="270" x2="300" y2="230" stroke="#0288D1" strokeWidth="2" />
    
    {/* T - Timekeeping Box */}
    <rect x="0" y="200" width="160" height="60" rx="5" ry="5" fill="#f3e5f5" stroke="#7B1FA2" strokeWidth="2" />
    <text x="80" y="225" textAnchor="middle" fontFamily="sans-serif" fontSize="16" fontWeight="bold" fill="#4A148C">
      T
    </text>
    <text x="80" y="245" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#4A148C">
      Timekeeping &
    </text>
    <text x="80" y="265" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#4A148C">
      Chronobiology
    </text>
    <line x1="160" y1="230" x2="260" y2="220" stroke="#7B1FA2" strokeWidth="2" />
  </svg>
);

const SignalingPathwaysDiagram = () => (
  <svg viewBox="0 0 600 350" className="w-full h-auto max-w-md mx-auto my-6">
    {/* Background */}
    <rect width="600" height="350" fill="#f5f7fa" rx="5" ry="5" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="18" fontWeight="bold" fill="#333">
      Cellular Signaling Pathways
    </text>
    
    {/* Cell Membrane */}
    <path d="M50,120 C100,110 200,100 300,105 C400,110 500,120 550,130 L550,180 C500,170 400,160 300,155 C200,160 100,170 50,180 Z" fill="#e3f2fd" stroke="#1976D2" strokeWidth="2" />
    
    {/* External Signal/Ligand */}
    <circle cx="130" cy="80" r="15" fill="#4CAF50" stroke="#2E7D32" strokeWidth="1.5" />
    <text x="130" y="60" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#2E7D32">
      External Signal
    </text>
    
    {/* Receptor */}
    <rect x="120" y="110" width="20" height="40" rx="2" ry="2" fill="#81C784" stroke="#2E7D32" strokeWidth="1.5" />
    <text x="130" y="165" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#2E7D32">
      Receptor
    </text>
    
    {/* Signaling Cascade */}
    <circle cx="130" cy="200" r="15" fill="#1976D2" stroke="#0D47A1" strokeWidth="1.5" />
    <circle cx="200" cy="240" r="15" fill="#1976D2" stroke="#0D47A1" strokeWidth="1.5" />
    <circle cx="270" cy="280" r="15" fill="#1976D2" stroke="#0D47A1" strokeWidth="1.5" />
    
    {/* Arrows */}
    <line x1="130" y1="95" x2="130" y2="110" stroke="#2E7D32" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="130" y1="150" x2="130" y2="185" stroke="#0D47A1" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="145" y1="200" x2="185" y2="240" stroke="#0D47A1" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="215" y1="240" x2="255" y2="280" stroke="#0D47A1" strokeWidth="2" markerEnd="url(#arrowhead)" />
    
    {/* Cellular Response */}
    <rect x="340" y="250" width="120" height="50" rx="5" ry="5" fill="#FFF9C4" stroke="#FBC02D" strokeWidth="1.5" />
    <text x="400" y="280" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#F57F17">
      Cellular Response
    </text>
    
    <line x1="285" y1="280" x2="340" y2="280" stroke="#0D47A1" strokeWidth="2" markerEnd="url(#arrowhead)" />
    
    {/* Labels for Signaling Components */}
    <text x="130" y="215" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#0D47A1">
      Messenger 1
    </text>
    <text x="200" y="255" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#0D47A1">
      Messenger 2
    </text>
    <text x="270" y="295" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#0D47A1">
      Messenger 3
    </text>
    
    {/* Arrow definition */}
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#333" />
      </marker>
    </defs>
    
    {/* Nucleus in the background */}
    <circle cx="480" cy="180" r="50" fill="#E1F5FE" stroke="#29B6F6" strokeWidth="1.5" opacity="0.5" />
    <text x="480" y="185" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#0288D1" opacity="0.7">
      Nucleus
    </text>
  </svg>
);

const SensingDiagram = () => (
  <svg viewBox="0 0 600 350" className="w-full h-auto max-w-md mx-auto my-6">
    {/* Background */}
    <rect width="600" height="350" fill="#f5f7fa" rx="5" ry="5" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="18" fontWeight="bold" fill="#333">
      Sensing & Metabolic Fuel-Gauging
    </text>
    
    {/* Cell Outlines */}
    <ellipse cx="200" cy="190" rx="120" ry="100" fill="#e8f5e9" stroke="#43A047" strokeWidth="2" />
    <text x="200" y="120" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#2E7D32">
      Healthy Cell
    </text>
    
    <ellipse cx="450" cy="190" rx="100" ry="90" fill="#e8f5e9" stroke="#43A047" strokeWidth="2" opacity="0.7" />
    <text x="450" y="120" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#EF5350">
      Aged/Dysregulated
    </text>
    
    {/* Nutrients Icons (outside cells) */}
    <rect x="100" y="50" width="20" height="20" fill="#FFC107" stroke="#FFA000" strokeWidth="1" />
    <text x="125" y="65" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#F57F17">
      Glucose
    </text>
    
    <rect x="150" y="50" width="20" height="10" fill="#8D6E63" stroke="#5D4037" strokeWidth="1" />
    <text x="175" y="65" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#5D4037">
      Amino Acids
    </text>
    
    <circle cx="220" cy="60" r="10" fill="#4CAF50" stroke="#2E7D32" strokeWidth="1" />
    <text x="235" y="65" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#2E7D32">
      Lipids
    </text>
    
    {/* Sensor Hub (Inside Healthy Cell) */}
    <circle cx="200" cy="190" r="30" fill="#81C784" stroke="#388E3C" strokeWidth="2" />
    <text x="200" y="194" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#1B5E20">
      Sensor Hub
    </text>
    
    {/* Arrows from Nutrients to Hub */}
    <line x1="130" y1="80" x2="180" y2="160" stroke="#43A047" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
    <line x1="170" y1="80" x2="190" y2="160" stroke="#43A047" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
    <line x1="220" y1="80" x2="210" y2="160" stroke="#43A047" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
    
    {/* Growth/Storage and Energy/Maintenance Icons */}
    <rect x="120" y="220" width="50" height="30" rx="5" ry="5" fill="#E6EE9C" stroke="#9E9D24" strokeWidth="1.5" />
    <text x="145" y="240" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#827717">
      Growth
    </text>
    
    <rect x="230" y="220" width="50" height="30" rx="5" ry="5" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1.5" />
    <text x="255" y="240" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#01579B">
      Maintenance
    </text>
    
    {/* Arrows from Hub to Functions */}
    <line x1="180" y1="210" x2="150" y2="220" stroke="#43A047" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
    <line x1="220" y1="210" x2="240" y2="220" stroke="#43A047" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
    
    {/* Dysregulated Cell Components */}
    <circle cx="450" cy="190" r="25" fill="#FFCDD2" stroke="#E57373" strokeWidth="2" />
    <text x="450" y="194" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#C62828">
      Dysregulated
    </text>
    
    <rect x="390" y="230" width="60" height="30" rx="5" ry="5" fill="#FFCCBC" stroke="#FF8A65" strokeWidth="1.5" />
    <text x="420" y="250" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#D84315">
      Imbalanced
    </text>
    
    <rect x="470" y="230" width="60" height="30" rx="5" ry="5" fill="#FFCCBC" stroke="#FF8A65" strokeWidth="1.5" />
    <text x="500" y="250" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#D84315">
      Inefficient
    </text>
    
    {/* Disrupted Arrows in Aged Cell */}
    <line x1="440" y1="210" x2="420" y2="230" stroke="#E57373" strokeWidth="1.5" strokeDasharray="3,2" markerEnd="url(#arrowhead)" />
    <line x1="460" y1="210" x2="490" y2="230" stroke="#E57373" strokeWidth="1.5" strokeDasharray="3,2" markerEnd="url(#arrowhead)" />
    
    {/* Legend */}
    <text x="75" y="295" textAnchor="start" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#333">
      Key Pathways:
    </text>
    <text x="75" y="315" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#333">
      • Insulin/IGF-1 — "Growth Mode"
    </text>
    <text x="75" y="335" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#333">
      • mTOR — "Build and Spend Mode"
    </text>
    <text x="350" y="315" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#333">
      • AMPK — "Energy Saving Mode"
    </text>
    <text x="350" y="335" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#333">
      • Sirtuins — "Maintenance & Resilience"
    </text>
    
    {/* Arrow definition */}
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#43A047" />
      </marker>
    </defs>
  </svg>
);

const HomeostasisDiagram = () => (
  <svg viewBox="0 0 600 350" className="w-full h-auto max-w-md mx-auto my-6">
    {/* Background */}
    <rect width="600" height="350" fill="#f5f7fa" rx="5" ry="5" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="18" fontWeight="bold" fill="#333">
      Homeostatic Defence & Repair
    </text>
    
    {/* Cell Outline */}
    <ellipse cx="300" cy="180" rx="170" ry="120" fill="#e3f2fd" stroke="#1976D2" strokeWidth="2" />
    
    {/* External Stressors */}
    <path d="M160,80 L170,100 L150,105 Z" fill="#EF5350" stroke="#C62828" strokeWidth="1" />
    <path d="M220,60 L235,75 L220,90 Z" fill="#EF5350" stroke="#C62828" strokeWidth="1" />
    <path d="M380,70 L400,80 L380,90 Z" fill="#EF5350" stroke="#C62828" strokeWidth="1" />
    <path d="M450,90 L440,110 L460,105 Z" fill="#EF5350" stroke="#C62828" strokeWidth="1" />
    
    <text x="300" y="70" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fill="#D32F2F">
      Stressors (ROS, Toxins, Radiation)
    </text>
    
    {/* Defense Shield */}
    <path d="M250,110 A100,70 0 0,1 350,110" fill="none" stroke="#1976D2" strokeWidth="3" />
    <path d="M230,120 A120,90 0 0,1 370,120" fill="none" stroke="#1976D2" strokeWidth="2" opacity="0.7" />
    <text x="300" y="100" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#0D47A1">
      Defense (NRF2)
    </text>
    
    {/* Repair Tools */}
    <circle cx="250" cy="170" r="20" fill="#7986CB" stroke="#3949AB" strokeWidth="1.5" />
    <path d="M240,160 L260,180 M240,180 L260,160" stroke="#3949AB" strokeWidth="2" />
    <text x="250" y="200" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#283593">
      DNA Repair
    </text>
    
    <circle cx="350" cy="170" r="20" fill="#7986CB" stroke="#3949AB" strokeWidth="1.5" />
    <rect x="340" y="160" width="20" height="20" fill="none" stroke="#3949AB" strokeWidth="2" />
    <text x="350" y="200" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#283593">
      Protein Quality
    </text>
    
    {/* Autophagy/Recycling */}
    <circle cx="300" cy="230" r="25" fill="#81D4FA" stroke="#0288D1" strokeWidth="1.5" />
    <path d="M285,230 A15,15 0 0,1 315,230 A15,15 0 0,1 285,230 Z" fill="none" stroke="#0288D1" strokeWidth="2" strokeDasharray="3,2" />
    <text x="300" y="265" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#01579B">
      Autophagy/Recycling
    </text>
    
    {/* Damaged Components */}
    <rect x="230" cy="150" width="10" height="10" fill="#FFCDD2" stroke="#E57373" strokeWidth="1" />
    <rect x="370" cy="180" width="10" height="10" fill="#FFCDD2" stroke="#E57373" strokeWidth="1" />
    <rect x="280" cy="210" width="10" height="10" fill="#FFCDD2" stroke="#E57373" strokeWidth="1" />
    
    {/* Legend */}
    <rect x="50" y="290" width="500" height="50" rx="5" ry="5" fill="#BBDEFB" fillOpacity="0.3" stroke="#1976D2" strokeWidth="1" />
    <text x="75" y="310" textAnchor="start" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#0D47A1">
      Key Pathways:
    </text>
    <text x="75" y="330" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#0D47A1">
      NRF2 — Antioxidant Defense   |   p53/p16 — Quality Control   |   Autophagy/Mitophagy — Cellular Recycling
    </text>
  </svg>
);

const InflammationDiagram = () => (
  <svg viewBox="0 0 600 350" className="w-full h-auto max-w-md mx-auto my-6">
    {/* Background */}
    <rect width="600" height="350" fill="#f5f7fa" rx="5" ry="5" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="18" fontWeight="bold" fill="#333">
      Inflammation & Immune Tone
    </text>
    
    {/* Panel 1: Youthful Immunity */}
    <rect x="50" y="70" width="200" height="200" rx="5" ry="5" fill="#fff3e0" stroke="#FF9800" strokeWidth="1.5" opacity="0.5" />
    <text x="150" y="90" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#E65100">
      Youthful Immunity
    </text>
    
    {/* Pathogen in Panel 1 */}
    <path d="M90,130 C100,120 110,125 120,130 C130,135 140,130 150,125" stroke="#4CAF50" strokeWidth="2" fill="none" />
    <circle cx="120" cy="130" r="15" fill="#4CAF50" fillOpacity="0.2" stroke="#2E7D32" strokeWidth="1" />
    <text x="120" y="155" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#2E7D32">
      Pathogen
    </text>
    
    {/* Immune Cells in Panel 1 - Sharp, Active Shapes */}
    <polygon points="160,120 175,110 190,120 175,130" fill="#1976D2" stroke="#0D47A1" strokeWidth="1.5" />
    <polygon points="200,140 215,130 230,140 215,150" fill="#1976D2" stroke="#0D47A1" strokeWidth="1.5" />
    <text x="190" y="155" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#0D47A1">
      Efficient Immune Cells
    </text>
    
    {/* Controlled Inflammation in Panel 1 */}
    <path d="M100,180 Q120,165 140,180 Q160,195 180,180" stroke="#EF6C00" strokeWidth="1.5" fill="none" />
    <text x="150" y="210" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#EF6C00">
      Controlled Inflammation
    </text>
    
    {/* Panel 2: Aged Immunity/Inflammaging */}
    <rect x="350" y="70" width="200" height="200" rx="5" ry="5" fill="#fff3e0" stroke="#FF9800" strokeWidth="1.5" opacity="0.5" />
    <text x="450" y="90" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#E65100">
      Aged Immunity / Inflammaging
    </text>
    
    {/* Same Pathogen in Panel 2 */}
    <path d="M390,130 C400,120 410,125 420,130 C430,135 440,130 450,125" stroke="#4CAF50" strokeWidth="2" fill="none" />
    <circle cx="420" cy="130" r="15" fill="#4CAF50" fillOpacity="0.2" stroke="#2E7D32" strokeWidth="1" />
    <text x="420" y="155" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#2E7D32">
      Pathogen
    </text>
    
    {/* Inefficient Immune Cells in Panel 2 - Duller, Slower Shapes */}
    <circle cx="460" cy="120" r="15" fill="#90CAF9" stroke="#1976D2" strokeWidth="1.5" />
    <circle cx="500" cy="140" r="15" fill="#90CAF9" stroke="#1976D2" strokeWidth="1.5" />
    <text x="480" y="155" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#1976D2">
      Inefficient Immune Cells
    </text>
    
    {/* Chronic Inflammation / Inflammaging in Panel 2 */}
    <path d="M370,180 Q400,160 430,180 Q460,200 490,180 Q520,160 550,180" stroke="#F4511E" strokeWidth="2.5" fill="none" />
    <path d="M380,200 Q410,180 440,200 Q470,220 500,200" stroke="#F4511E" strokeWidth="2" fill="none" />
    <text x="450" y="230" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#D84315">
      Chronic Inflammation / Inflammaging
    </text>
    
    {/* Legend */}
    <rect x="50" y="290" width="500" height="50" rx="5" ry="5" fill="#FFE0B2" fillOpacity="0.3" stroke="#FF9800" strokeWidth="1" />
    <text x="75" y="310" textAnchor="start" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#E65100">
      Key Concepts:
    </text>
    <text x="75" y="330" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#E65100">
      Inflammaging — Chronic, low-grade inflammation   |   Immunosenescence — Declining immune effectiveness
    </text>
  </svg>
);

const FibrosisDiagram = () => (
  <svg viewBox="0 0 600 350" className="w-full h-auto max-w-md mx-auto my-6">
    {/* Background */}
    <rect width="600" height="350" fill="#f5f7fa" rx="5" ry="5" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="18" fontWeight="bold" fill="#333">
      Fibrosis & Structural Remodeling
    </text>
    
    {/* Panel 1: Healthy Tissue Structure */}
    <rect x="50" y="70" width="200" height="200" rx="5" ry="5" fill="#e1f5fe" stroke="#0288D1" strokeWidth="1.5" opacity="0.5" />
    <text x="150" y="90" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#01579B">
      Healthy Tissue
    </text>
    
    {/* Cells in Healthy Tissue */}
    <circle cx="100" cy="130" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    <circle cx="140" cy="110" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    <circle cx="180" cy="130" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    <circle cx="120" cy="170" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    <circle cx="160" cy="160" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    <circle cx="200" cy="170" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    
    {/* Balanced ECM in Healthy Tissue */}
    <line x1="100" y1="130" x2="140" y2="110" stroke="#4FC3F7" strokeWidth="1" />
    <line x1="140" y1="110" x2="180" y2="130" stroke="#4FC3F7" strokeWidth="1" />
    <line x1="120" y1="170" x2="100" y2="130" stroke="#4FC3F7" strokeWidth="1" />
    <line x1="120" y1="170" x2="160" y2="160" stroke="#4FC3F7" strokeWidth="1" />
    <line x1="160" y1="160" x2="200" y2="170" stroke="#4FC3F7" strokeWidth="1" />
    <line x1="180" y1="130" x2="160" y2="160" stroke="#4FC3F7" strokeWidth="1" />
    <line x1="140" y1="110" x2="160" y2="160" stroke="#4FC3F7" strokeWidth="1" />
    
    {/* Text for Balanced ECM */}
    <text x="150" y="220" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#0288D1">
      Balanced ECM & Cellularity
    </text>
    <text x="150" y="240" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#0288D1">
      Normal Tissue Function
    </text>
    
    {/* Panel 2: Fibrotic Tissue */}
    <rect x="350" y="70" width="200" height="200" rx="5" ry="5" fill="#e1f5fe" stroke="#0288D1" strokeWidth="1.5" opacity="0.5" />
    <text x="450" y="90" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#01579B">
      Fibrotic Tissue
    </text>
    
    {/* Cells in Fibrotic Tissue - Fewer */}
    <circle cx="400" cy="120" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    <circle cx="470" cy="110" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    <circle cx="440" cy="170" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    <circle cx="520" cy="160" r="15" fill="#B3E5FC" stroke="#0288D1" strokeWidth="1" />
    
    {/* Excessive ECM/Collagen in Fibrotic Tissue */}
    <line x1="400" y1="120" x2="470" y2="110" stroke="#01579B" strokeWidth="3" />
    <line x1="400" y1="120" x2="440" y2="170" stroke="#01579B" strokeWidth="3" />
    <line x1="470" y1="110" x2="520" y2="160" stroke="#01579B" strokeWidth="3" />
    <line x1="440" y1="170" x2="520" y2="160" stroke="#01579B" strokeWidth="3" />
    <line x1="470" y1="110" x2="440" y2="170" stroke="#01579B" strokeWidth="3" />
    
    {/* Crosslinks in Fibrotic Tissue */}
    <circle cx="435" cy="140" r="5" fill="#0277BD" stroke="none" />
    <circle cx="485" cy="135" r="5" fill="#0277BD" stroke="none" />
    <circle cx="480" cy="170" r="5" fill="#0277BD" stroke="none" />
    
    {/* Text for Fibrotic Tissue */}
    <text x="450" y="220" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#01579B">
      Excessive Collagen & Crosslinks
    </text>
    <text x="450" y="240" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#01579B">
      Impaired Tissue Function
    </text>
    
    {/* Legend */}
    <rect x="50" y="290" width="500" height="50" rx="5" ry="5" fill="#B3E5FC" fillOpacity="0.3" stroke="#0288D1" strokeWidth="1" />
    <text x="75" y="310" textAnchor="start" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#01579B">
      Key Concepts:
    </text>
    <text x="75" y="330" textAnchor="start" fontFamily="sans-serif" fontSize="12" fill="#01579B">
      ECM Balance — Matrix production vs. degradation   |   AGEs — Advanced Glycation End-products   |   TGF-β — Major fibrosis driver
    </text>
  </svg>
);

const TimeKeepingDiagram = () => (
  <svg viewBox="0 0 600 350" className="w-full h-auto max-w-md mx-auto my-6">
    {/* Background */}
    <rect width="600" height="350" fill="#f5f7fa" rx="5" ry="5" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="18" fontWeight="bold" fill="#333">
      Timekeeping & Chronobiology
    </text>
    
    {/* Central Clock Icon */}
    <circle cx="300" cy="125" r="50" fill="#f3e5f5" stroke="#7B1FA2" strokeWidth="2" />
    <circle cx="300" cy="125" r="45" fill="none" stroke="#7B1FA2" strokeWidth="1" />
    
    {/* Clock Hands */}
    <line x1="300" y1="125" x2="300" y2="90" stroke="#4A148C" strokeWidth="2.5" />
    <line x1="300" y1="125" x2="330" y2="125" stroke="#4A148C" strokeWidth="2" />
    
    {/* Clock Numbers */}
    <text x="300" y="85" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#4A148C">12</text>
    <text x="335" y="125" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#4A148C">3</text>
    <text x="300" y="165" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#4A148C">6</text>
    <text x="265" y="125" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#4A148C">9</text>
    
    <text x="300" y="200" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#4A148C">
      Master Circadian Clock
    </text>
    
    {/* Peripheral Clocks */}
    <circle cx="150" cy="225" r="30" fill="#f3e5f5" stroke="#7B1FA2" strokeWidth="1.5" opacity="0.8" />
    <text x="150" y="230" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#4A148C">
      Liver
    </text>
    
    <circle cx="230" cy="275" r="30" fill="#f3e5f5" stroke="#7B1FA2" strokeWidth="1.5" opacity="0.8" />
    <text x="230" y="280" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#4A148C">
      Muscle
    </text>
    
    <circle cx="370" cy="275" r="30" fill="#f3e5f5" stroke="#7B1FA2" strokeWidth="1.5" opacity="0.8" />
    <text x="370" y="280" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#4A148C">
      Adipose
    </text>
    
    <circle cx="450" cy="225" r="30" fill="#f3e5f5" stroke="#7B1FA2" strokeWidth="1.5" opacity="0.8" />
    <text x="450" y="230" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#4A148C">
      Gut
    </text>
    
    {/* Synchronizing Arrows */}
    <line x1="288" y1="170" x2="240" y2="250" stroke="#7B1FA2" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
    <line x1="265" y1="150" x2="170" y2="205" stroke="#7B1FA2" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
    <line x1="335" y1="150" x2="430" y2="205" stroke="#7B1FA2" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
    <line x1="312" y1="170" x2="360" y2="250" stroke="#7B1FA2" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
    
    {/* Environmental Cues */}
    <circle cx="150" cy="75" r="20" fill="#FFF176" stroke="#FBC02D" strokeWidth="1.5" />
    <text x="150" y="79" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#F57F17">
      Light
    </text>
    
    <rect x="450" cy="75" width="30" height="20" rx="3" ry="3" fill="#A5D6A7" stroke="#43A047" strokeWidth="1.5" />
    <text x="465" y="89" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#2E7D32">
      Food
    </text>
    
    {/* Light Cue to Master Clock */}
    <line x1="170" y1="75" x2="250" y2="110" stroke="#FBC02D" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
    
    {/* Food Cue to Master Clock and Peripheral Clocks */}
    <line x1="450" y1="95" x2="450" y2="195" stroke="#43A047" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
    <line x1="430" y1="75" x2="350" y2="110" stroke="#43A047" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
    
    {/* Legend */}
    <rect x="50" y="315" width="500" height="25" rx="5" ry="5" fill="#F3E5F5" fillOpacity="0.3" stroke="#7B1FA2" strokeWidth="1" />
    <text x="300" y="332" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fill="#4A148C">
      Key Clock Genes: CLOCK, BMAL1, PER, CRY — Drive ~24 hour oscillations in cellular function
    </text>
    
    {/* Arrow definitions */}
    <defs>
      <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#7B1FA2" />
      </marker>
      <marker id="yellowArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#FBC02D" />
      </marker>
      <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#43A047" />
      </marker>
    </defs>
  </svg>
);

const ClinicalApplicationsDiagram = () => (
  <svg viewBox="0 0 600 350" className="w-full h-auto max-w-md mx-auto my-6">
    {/* Background */}
    <rect width="600" height="350" fill="#f5f7fa" rx="5" ry="5" />
    
    {/* Title */}
    <text x="300" y="40" textAnchor="middle" fontFamily="sans-serif" fontSize="18" fontWeight="bold" fill="#333">
      Clinical Applications of the SHIFT Framework
    </text>
    
    {/* Central Pentagon */}
    <polygon points="300,100 350,130 330,190 270,190 250,130" 
             fill="#e8eaf6" stroke="#3F51B5" strokeWidth="2" />
    <text x="300" y="160" textAnchor="middle" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="#3F51B5">
      Patient
    </text>
    
    {/* Five Domains with Assessment and Intervention */}
    
    {/* S Domain */}
    <rect x="120" y="80" width="100" height="50" rx="5" ry="5" fill="#e8f5e9" stroke="#43A047" strokeWidth="1.5" />
    <text x="170" y="105" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#2E7D32">
      Sensing
    </text>
    <text x="170" y="120" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#2E7D32">
      Assess: Insulin, Glucose
    </text>
    <line x1="220" y1="105" x2="250" y2="130" stroke="#43A047" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
    
    <rect x="50" y="140" width="100" height="30" rx="3" ry="3" fill="#c8e6c9" stroke="#43A047" strokeWidth="1" />
    <text x="100" y="160" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#2E7D32">
      Interventions: Fasting,
    </text>
    <text x="100" y="172" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#2E7D32">
      Metformin
    </text>
    <line x1="150" y1="155" x2="170" y2="155" stroke="#43A047" strokeWidth="1" markerEnd="url(#greenArrow)" />
    <line x1="170" y1="155" x2="170" y2="130" stroke="#43A047" strokeWidth="1" markerEnd="url(#greenArrow)" />
    
    {/* H Domain */}
    <rect x="440" y="80" width="100" height="50" rx="5" ry="5" fill="#e3f2fd" stroke="#1976D2" strokeWidth="1.5" />
    <text x="490" y="105" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#0D47A1">
      Homeostasis
    </text>
    <text x="490" y="120" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#0D47A1">
      Assess: 8-OHdG, GSH
    </text>
    <line x1="440" y1="105" x2="350" y2="130" stroke="#1976D2" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
    
    <rect x="460" y="140" width="100" height="30" rx="3" ry="3" fill="#bbdefb" stroke="#1976D2" strokeWidth="1" />
    <text x="510" y="160" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#0D47A1">
      Interventions: NRF2
    </text>
    <text x="510" y="172" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#0D47A1">
      activators, Exercise
    </text>
    <line x1="460" y1="155" x2="440" y2="155" stroke="#1976D2" strokeWidth="1" markerEnd="url(#blueArrow)" />
    <line x1="440" y1="155" x2="440" y2="130" stroke="#1976D2" strokeWidth="1" markerEnd="url(#blueArrow)" />
    
    {/* I Domain */}
    <rect x="380" y="240" width="100" height="50" rx="5" ry="5" fill="#fff3e0" stroke="#F57C00" strokeWidth="1.5" />
    <text x="430" y="265" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#E65100">
      Inflammation
    </text>
    <text x="430" y="280" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#E65100">
      Assess: CRP, IL-6
    </text>
    <line x1="380" y1="250" x2="330" y2="220" stroke="#F57C00" strokeWidth="1.5" markerEnd="url(#orangeArrow)" />
    
    <rect x="490" y="230" width="100" height="30" rx="3" ry="3" fill="#ffe0b2" stroke="#F57C00" strokeWidth="1" />
    <text x="540" y="248" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#E65100">
      Interventions: SPMs,
    </text>
    <text x="540" y="260" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#E65100">
      Anti-inflammatories
    </text>
    <line x1="490" y1="245" x2="480" y2="245" stroke="#F57C00" strokeWidth="1" markerEnd="url(#orangeArrow)" />
    <line x1="480" y1="245" x2="480" y2="250" stroke="#F57C00" strokeWidth="1" markerEnd="url(#orangeArrow)" />
    
    {/* F Domain */}
    <rect x="120" y="240" width="100" height="50" rx="5" ry="5" fill="#e1f5fe" stroke="#0288D1" strokeWidth="1.5" />
    <text x="170" y="265" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#01579B">
      Fibrosis
    </text>
    <text x="170" y="280" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#01579B">
      Assess: AGEs, Elasticity
    </text>
    <line x1="220" y1="250" x2="270" y2="220" stroke="#0288D1" strokeWidth="1.5" markerEnd="url(#lightBlueArrow)" />
    
    <rect x="10" y="230" width="100" height="30" rx="3" ry="3" fill="#b3e5fc" stroke="#0288D1" strokeWidth="1" />
    <text x="60" y="248" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#01579B">
      Interventions: Collagen
    </text>
    <text x="60" y="260" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#01579B">
      support, Massage
    </text>
    <line x1="110" y1="245" x2="120" y2="245" stroke="#0288D1" strokeWidth="1" markerEnd="url(#lightBlueArrow)" />
    <line x1="120" y1="245" x2="120" y2="250" stroke="#0288D1" strokeWidth="1" markerEnd="url(#lightBlueArrow)" />
    
    {/* T Domain - Middle Bottom */}
    <rect x="250" y="300" width="100" height="50" rx="5" ry="5" fill="#f3e5f5" stroke="#7B1FA2" strokeWidth="1.5" />
    <text x="300" y="325" textAnchor="middle" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#4A148C">
      Timekeeping
    </text>
    <text x="300" y="340" textAnchor="middle" fontFamily="sans-serif" fontSize="10" fill="#4A148C">
      Assess: Melatonin, HRV
    </text>
    <line x1="300" y1="300" x2="300" y2="190" stroke="#7B1FA2" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
    
    {/* Arrow definitions */}
    <defs>
      <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#43A047" />
      </marker>
      <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#1976D2" />
      </marker>
      <marker id="orangeArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#F57C00" />
      </marker>
      <marker id="lightBlueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#0288D1" />
      </marker>
      <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#7B1FA2" />
      </marker>
    </defs>
  </svg>
);

function ShiftFrameworkHealthLongevity() {
  const [_, navigate] = useLocation();
  
  // Lecture slides data
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Decoding Longevity: Introducing the SHIFT Framework for Clinicians",
      content: (
        <>
          <p className="text-lg">A New Lens for Understanding and Addressing Healthy Aging</p>
          
          <TitleDiagram />
          
          <p>Welcome to this introduction to the SHIFT framework - a structured approach to understanding and addressing the cellular basis of aging and longevity. This framework organizes the complex biology of aging into actionable domains for clinical intervention.</p>
          
          <KeyTakeaway>
            The SHIFT framework provides clinicians with a practical structure for navigating the cellular mechanisms of aging and applying evidence-based interventions to promote healthspan.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 2,
      title: "The Quest for Longevity: More Than Just Adding Years",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">What is Longevity Medicine?</h3>
          
          <ul className="list-disc ml-6 mb-4 space-y-2">
            <li>Shifting focus from <strong>Lifespan</strong> (years lived) to <strong>Healthspan</strong> (healthy years lived).</li>
            <li>Moving from reactive disease treatment to <strong>proactive prevention</strong> and health optimization.</li>
            <li>The Goal: Not just adding years to life, but <strong>adding life to years</strong>.</li>
          </ul>
          
          <HealthspanDiagram />
          
          <p>Longevity medicine represents a paradigm shift in healthcare - focusing on extending the period of life spent in good health rather than simply extending life itself. This approach requires understanding the fundamental biological processes that drive aging and age-related diseases.</p>
        </>
      )
    },
    {
      id: 3,
      title: "The 'Healthspan Gap': Why We Need a New Approach",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">The Modern Challenge: Living Longer, But Are We Living Healthier?</h3>
          
          <p className="mb-4">Globally, people are living longer, but often with a significant burden of chronic, age-related diseases (e.g., heart disease, diabetes, neurodegeneration). This widening gap between total lifespan and healthy years lived represents the "Healthspan Gap."</p>
          
          <HealthspanGapDiagram />
          
          <p>Traditional medicine excels at treating established diseases but often struggles to address their underlying causes. Longevity medicine aims to target the aging process itself – the root cause of many age-related conditions – potentially narrowing the healthspan gap.</p>
          
          <KeyTakeaway>
            The growing "healthspan gap" highlights the need for approaches that address the fundamental processes of aging rather than just treating individual diseases as they appear.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "The Complexity of Aging: A Cellular Perspective",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Aging Isn't Just About Wrinkles – It Starts in Our Cells</h3>
          
          <p className="mb-4">Aging is fundamentally a cellular and molecular process. We can think of the body as a city and cells as buildings - if individual buildings (cells) start to crumble, the whole city's function declines.</p>
          
          <CellularPerspectiveDiagram />
          
          <p>Many interconnected biological processes are involved in aging - like an orchestra with many sections that must play in harmony. This complexity requires a structured way to understand and address it clinically.</p>
          
          <KeyTakeaway>
            Understanding aging at the cellular level provides a foundation for more targeted and effective interventions that can influence multiple disease processes simultaneously.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 5,
      title: "Introducing the SHIFT Framework",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">The SHIFT Framework: Your Clinical Compass for Longevity</h3>
          
          <p className="mb-4">The SHIFT framework provides a structured way to understand the key biological domains that change with age and offers a lens to organize complex aging biology into actionable insights.</p>
          
          <ShiftFrameworkDiagram />
          
          <p><strong>SHIFT</strong> stands for:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li><strong>S</strong> – <strong>S</strong>ensing & Metabolic Fuel-Gauging</li>
            <li><strong>H</strong> – <strong>H</strong>omeostatic Defence & Repair</li>
            <li><strong>I</strong> – <strong>I</strong>nflammation & Immune Tone</li>
            <li><strong>F</strong> – <strong>F</strong>ibrosis / Structural Remodeling</li>
            <li><strong>T</strong> – <strong>T</strong>imekeeping & Chronobiology</li>
          </ul>
          
          <KeyTakeaway>
            The five domains of the SHIFT framework cover the primary cellular processes that govern aging, providing a comprehensive map for clinical assessment and intervention.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 6,
      title: "The Language of Cells: Signaling Pathways",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">How Cells "Talk": The Crucial Role of Signaling Pathways</h3>
          
          <p className="mb-4">Cells communicate and coordinate their actions through signaling pathways - like internal messaging systems that control everything from growth and energy use to repair and stress response.</p>
          
          <SignalingPathwaysDiagram />
          
          <p>Dysregulation of these pathways is a root cause of cellular aging and disease. The SHIFT framework helps us categorize and understand key groups of these pathways, making them more accessible for clinical intervention.</p>
          
          <KeyTakeaway>
            Cellular signaling pathways function like a cellular communication network. When these networks become dysregulated with age, they contribute to disease. Many longevity interventions work by optimizing these signaling networks.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 7,
      title: "S – Sensing & Metabolic Fuel-Gauging",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Sensing & Metabolic Fuel-Gauging: The Cellular Economy</h3>
          
          <p className="mb-3"><strong>Core Concept:</strong> How cells sense nutrient availability (like glucose and amino acids) and manage their energy budget.</p>
          
          <p className="mb-3"><strong>Analogy:</strong> The cell's "Accountants" or "Fuel Gauge" – constantly monitoring supply and demand.</p>
          
          <SensingDiagram />
          
          <p><strong>Why it matters in aging:</strong> With age, these sensing systems can become dysregulated, like a faulty fuel gauge or an accountant making bad investment decisions, leading to metabolic problems.</p>
          
          <KeyTakeaway>
            Nutrient sensing pathways evolved to optimize survival in environments with variable food availability. Modern constant overnutrition may contribute to accelerated aging by keeping these pathways chronically activated.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 8,
      title: "S – Sensing: Key Pathways & Clinical Links",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Key Sensing Pathways</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
            <div className="bg-green-50 p-3 rounded-md">
              <p className="font-medium text-green-800">Insulin/IGF-1</p>
              <p className="text-sm text-green-700">Signals nutrient abundance, promotes growth. (Think: "Growth mode")</p>
            </div>
            <div className="bg-green-50 p-3 rounded-md">
              <p className="font-medium text-green-800">mTOR</p>
              <p className="text-sm text-green-700">Central controller of cell growth and metabolism, also sensitive to nutrients. (Think: "Build and spend mode")</p>
            </div>
            <div className="bg-green-50 p-3 rounded-md">
              <p className="font-medium text-green-800">AMPK</p>
              <p className="text-sm text-green-700">Senses low energy, activates energy production and conservation. (Think: "Energy saving & efficiency mode")</p>
            </div>
            <div className="bg-green-50 p-3 rounded-md">
              <p className="font-medium text-green-800">Sirtuins</p>
              <p className="text-sm text-green-700">NAD+-dependent "efficiency regulators," link metabolism to cellular health and stress resistance. (Think: "Maintenance & resilience crew")</p>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold mb-2">Clinical Implications</h3>
          
          <p className="mb-3">These pathways are implicated in metabolic syndrome, Type 2 Diabetes, obesity, and many age-related conditions.</p>
          
          <div className="bg-green-50 p-4 rounded-md">
            <p className="font-medium text-green-800 mb-2">Key Interventions:</p>
            <ul className="list-disc ml-5 text-green-700 space-y-1">
              <li>Time-restricted eating / Intermittent fasting</li>
              <li>Metformin (AMPK activator)</li>
              <li>NAD+ precursors (NMN, NR)</li>
              <li>Exercise (activates AMPK and sirtuins)</li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 9,
      title: "H – Homeostatic Defence & Repair",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Homeostatic Defence & Repair: The Cellular Maintenance Crew</h3>
          
          <p className="mb-3"><strong>Core Concept:</strong> The cell's ability to protect itself from damage, repair what's broken, and clear out waste.</p>
          
          <p className="mb-3"><strong>Analogy:</strong> The cell's "Maintenance & Cleanup Crew" or "Internal Repair Service."</p>
          
          <HomeostasisDiagram />
          
          <p><strong>Why it matters in aging:</strong> These systems become less efficient with age, leading to an accumulation of damage – like a city with failing infrastructure and overflowing waste.</p>
          
          <KeyTakeaway>
            Robust homeostatic defense and repair mechanisms are essential for cellular longevity. Many age-related diseases involve the progressive failure of these systems to maintain cellular integrity and function.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 10,
      title: "H – Defence & Repair: Key Pathways & Clinical Links",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Key Defense & Repair Pathways</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-4">
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="font-medium text-blue-800">NRF2</p>
              <p className="text-sm text-blue-700">Master regulator of antioxidant defenses. (Think: "Chief of cellular defense against oxidative stress")</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="font-medium text-blue-800">p53 / p16<sup>INK4A</sup></p>
              <p className="text-sm text-blue-700">Tumor suppressors that halt division of damaged cells (senescence) or trigger self-destruction (apoptosis). (Think: "Quality control inspectors")</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="font-medium text-blue-800">Autophagy / Mitophagy</p>
              <p className="text-sm text-blue-700">Cellular recycling systems that clear out damaged proteins and organelles. (Think: "Waste disposal and recycling plants")</p>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold mb-2">Clinical Implications</h3>
          
          <p className="mb-3">These pathways are linked to oxidative damage, cancer prevention/acceleration, neurodegeneration, and overall resilience.</p>
          
          <div className="bg-blue-50 p-4 rounded-md">
            <p className="font-medium text-blue-800 mb-2">Key Interventions:</p>
            <ul className="list-disc ml-5 text-blue-700 space-y-1">
              <li>NRF2 activators (sulforaphane from broccoli sprouts, curcumin)</li>
              <li>Autophagy enhancers (fasting, exercise, spermidine)</li>
              <li>Heat shock protein induction (sauna use, heat conditioning)</li>
              <li>DNA repair support (glycine, micronutrients)</li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 11,
      title: "I – Inflammation & Immune Tone",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Inflammation & Immune Tone: The Body's Security System</h3>
          
          <p className="mb-3"><strong>Core Concept:</strong> Regulating the body's inflammatory responses and the overall "alertness" and effectiveness of the immune system.</p>
          
          <p className="mb-3"><strong>Analogy:</strong> The body's "Security System & Emergency Responders."</p>
          
          <InflammationDiagram />
          
          <p><strong>Why it matters in aging:</strong></p>
          <ul className="list-disc ml-6 space-y-1">
            <li><strong>"Inflammaging":</strong> Chronic, low-level inflammation – a fire alarm always smoldering.</li>
            <li><strong>Immunosenescence:</strong> Decline in immune effectiveness – an aging security force.</li>
          </ul>
          
          <KeyTakeaway>
            Immune tone represents the balance between necessary immune surveillance and excessive inflammatory activation. Disruption of this balance contributes to many age-related diseases and is a key target for longevity interventions.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 12,
      title: "I – Inflammation: Key Mechanisms & Clinical Links",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Key Inflammatory Mechanisms</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
            <div className="bg-orange-50 p-3 rounded-md">
              <p className="font-medium text-orange-800">Senescent Cells</p>
              <p className="text-sm text-orange-700">Permanently growth-arrested cells that secrete inflammatory factors (SASP). Accumulate with age and drive tissue dysfunction.</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-md">
              <p className="font-medium text-orange-800">Inflammasome Activation</p>
              <p className="text-sm text-orange-700">Protein complexes that trigger inflammatory responses. NLRP3 inflammasome becomes more easily activated with age.</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-md">
              <p className="font-medium text-orange-800">Resolution Pathways</p>
              <p className="text-sm text-orange-700">Active processes that turn off inflammation using specialized pro-resolving mediators (SPMs). These become less efficient with age.</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-md">
              <p className="font-medium text-orange-800">T-Cell Exhaustion</p>
              <p className="text-sm text-orange-700">Immune cells that become dysfunctional due to chronic antigen exposure. Contributes to declining immune surveillance.</p>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold mb-2">Clinical Implications</h3>
          
          <p className="mb-3">These processes contribute to cardiovascular disease, neurodegeneration, autoimmunity, and increased cancer risk.</p>
          
          <div className="bg-orange-50 p-4 rounded-md">
            <p className="font-medium text-orange-800 mb-2">Key Interventions:</p>
            <ul className="list-disc ml-5 text-orange-700 space-y-1">
              <li>Senolytics (compounds that selectively eliminate senescent cells)</li>
              <li>Resolution-promoting therapies (SPMs, omega-3 fatty acids)</li>
              <li>Inflammasome inhibitors (polyphenols like quercetin)</li>
              <li>Thymic rejuvenation (zinc, arginine, thymosin-α1)</li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 13,
      title: "F – Fibrosis & Structural Remodeling",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Fibrosis & Structural Remodeling: Tissue Architecture</h3>
          
          <p className="mb-3"><strong>Core Concept:</strong> The balance between necessary structural support and excessive stiffening or remodeling of tissues.</p>
          
          <p className="mb-3"><strong>Analogy:</strong> The "Scaffolding and Construction" of the cellular environment.</p>
          
          <FibrosisDiagram />
          
          <p><strong>Why it matters in aging:</strong> With age, tissues tend to become stiffer and less elastic due to changes in extracellular matrix composition, crosslinking, and the accumulation of advanced glycation end-products (AGEs).</p>
          
          <KeyTakeaway>
            Fibrosis and structural remodeling represent fundamental contributors to age-related functional decline. Preventing excessive fibrosis and maintaining appropriate tissue architecture are crucial for preserving organ function with age.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 14,
      title: "F – Fibrosis: Key Mechanisms & Clinical Links",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Key Fibrosis Mechanisms</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
            <div className="bg-cyan-50 p-3 rounded-md">
              <p className="font-medium text-cyan-800">AGE Formation</p>
              <p className="text-sm text-cyan-700">Advanced Glycation End-products form when sugars react with proteins, creating crosslinks that stiffen tissues. Accumulate with age and in diabetes.</p>
            </div>
            <div className="bg-cyan-50 p-3 rounded-md">
              <p className="font-medium text-cyan-800">TGF-β Signaling</p>
              <p className="text-sm text-cyan-700">Transforming Growth Factor-beta is a master regulator of fibrosis. Its activity tends to increase with age, promoting excessive matrix deposition.</p>
            </div>
            <div className="bg-cyan-50 p-3 rounded-md">
              <p className="font-medium text-cyan-800">MMP/TIMP Imbalance</p>
              <p className="text-sm text-cyan-700">Matrix Metalloproteinases (MMPs) degrade ECM, while Tissue Inhibitors of MMPs (TIMPs) block this activity. Aging shifts toward reduced matrix turnover.</p>
            </div>
            <div className="bg-cyan-50 p-3 rounded-md">
              <p className="font-medium text-cyan-800">Mechanical Stimulation</p>
              <p className="text-sm text-cyan-700">Physical forces influence extracellular matrix organization. Reduced movement with age can promote fibrotic changes and adhesions.</p>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold mb-2">Clinical Implications</h3>
          
          <p className="mb-3">These processes contribute to arterial stiffening, cardiac fibrosis, pulmonary fibrosis, skin aging, and joint deterioration.</p>
          
          <div className="bg-cyan-50 p-4 rounded-md">
            <p className="font-medium text-cyan-800 mb-2">Key Interventions:</p>
            <ul className="list-disc ml-5 text-cyan-700 space-y-1">
              <li>AGE reduction strategies (dietary modification, AGE inhibitors like carnosine)</li>
              <li>Collagen-supporting nutrients (vitamin C, copper, glycine)</li>
              <li>Anti-fibrotic botanicals (curcumin, milk thistle, ginseng)</li>
              <li>Mechanical stimulation (resistance training, soft tissue mobilization)</li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 15,
      title: "T – Timekeeping & Chronobiology",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Timekeeping & Chronobiology: Cellular Rhythms</h3>
          
          <p className="mb-3"><strong>Core Concept:</strong> The coordination of cellular processes with environmental cycles, particularly the 24-hour light/dark cycle.</p>
          
          <p className="mb-3"><strong>Analogy:</strong> The body's "Master Clock and Timing System."</p>
          
          <TimeKeepingDiagram />
          
          <p><strong>Why it matters in aging:</strong> With age, circadian rhythms become weaker and less responsive to synchronizing cues, leading to fragmented activity patterns, disrupted sleep, and impaired temporal coordination of physiological processes.</p>
          
          <KeyTakeaway>
            Robust circadian rhythms are essential for coordinating biochemical and physiological processes. Preserving and enhancing these biological timekeeping mechanisms may be critical for healthy aging.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 16,
      title: "T – Timekeeping: Key Mechanisms & Clinical Links",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">Key Chronobiological Mechanisms</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
            <div className="bg-purple-50 p-3 rounded-md">
              <p className="font-medium text-purple-800">Clock Genes</p>
              <p className="text-sm text-purple-700">Core oscillatory machinery (CLOCK, BMAL1, PER, CRY) that drives circadian rhythms. This molecular clockwork becomes less robust with age.</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-md">
              <p className="font-medium text-purple-800">Light Entrainment</p>
              <p className="text-sm text-purple-700">Light exposure, particularly in the morning, synchronizes the master clock in the suprachiasmatic nucleus. Aging eyes admit less light, reducing entrainment signals.</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-md">
              <p className="font-medium text-purple-800">Peripheral Clocks</p>
              <p className="text-sm text-purple-700">Tissue-specific circadian oscillators present in nearly all cells. These become less synchronized with age, leading to temporal chaos.</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-md">
              <p className="font-medium text-purple-800">Feeding-Fasting Cycles</p>
              <p className="text-sm text-purple-700">Meal timing acts as a powerful synchronizer for peripheral clocks, especially in metabolic tissues. Regular patterns help maintain robust rhythms.</p>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold mb-2">Clinical Implications</h3>
          
          <p className="mb-3">Circadian disruption is linked to metabolic syndrome, cardiovascular disease, cancer risk, cognitive decline, and mood disorders.</p>
          
          <div className="bg-purple-50 p-4 rounded-md">
            <p className="font-medium text-purple-800 mb-2">Key Interventions:</p>
            <ul className="list-disc ml-5 text-purple-700 space-y-1">
              <li>Light hygiene (morning bright light, evening blue light avoidance)</li>
              <li>Time-restricted eating (limiting food intake to an 8-10 hour window)</li>
              <li>Circadian-supportive supplements (low-dose melatonin, REV-ERB agonists)</li>
              <li>Temperature rhythms (evening temperature reduction to enhance sleep)</li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 17,
      title: "Integrating the SHIFT Framework in Clinical Practice",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-3">A Practical Approach to Clinical Implementation</h3>
          
          <p className="mb-4">The SHIFT framework provides a comprehensive approach to addressing the cellular basis of aging and age-related diseases. In clinical practice, these domains frequently interact and overlap, requiring an integrated approach.</p>
          
          <ClinicalApplicationsDiagram />
          
          <div className="bg-gray-100 p-4 my-4 rounded-md">
            <h4 className="font-semibold mb-2">Clinical Approach to the SHIFT Framework</h4>
            <ol className="list-decimal ml-6 space-y-1">
              <li><strong>Assessment:</strong> Evaluate each domain through targeted history, examination, and biomarkers</li>
              <li><strong>Prioritization:</strong> Identify the most dysregulated domains based on patient-specific findings</li>
              <li><strong>Intervention sequencing:</strong> Address fundamental domains first (often Sensing and Timekeeping)</li>
              <li><strong>Monitoring:</strong> Track domain-specific biomarkers and clinical parameters to assess response</li>
              <li><strong>Adjustment:</strong> Refine interventions based on response patterns and emergent priorities</li>
            </ol>
          </div>
          
          <KeyTakeaway>
            The SHIFT framework provides a practical scaffold for organizing clinical assessment and intervention in longevity medicine. By systematically addressing each domain while recognizing their interactions, clinicians can develop comprehensive, personalized approaches to promoting healthy aging.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 18,
      title: "SHIFT Framework Diagnostic Testing Guide",
      content: (
        <>
          <h3 className="text-xl font-semibold mb-4">Laboratory Assessment Across SHIFT Domains</h3>
          
          <p className="mb-4 text-gray-700">This comprehensive testing panel allows clinicians to assess and monitor each pillar of the SHIFT framework systematically.</p>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Sensing */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <h4 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                <span className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">S</span>
                Sensing / Metabolic Fuel Gauging
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-green-700 mb-2">Glucose & Insulin Regulation</h5>
                  <ul className="text-sm text-green-600 space-y-1">
                    <li>• HbA1c</li>
                    <li>• Fasting Insulin</li>
                    <li>• Triglycerides</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-green-700 mb-2">Advanced Metabolic</h5>
                  <ul className="text-sm text-green-600 space-y-1">
                    <li>• Organic Acid Testing (Krebs Cycle)</li>
                    <li>• Mitoswab (Electron Transport Chain)</li>
                    <li>• Advanced Lipid Panel (Lp(a), ApoB)</li>
                    <li>• Hormones (Estrogen, Testosterone, Thyroid)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Homeostasis */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <h4 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
                <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">H</span>
                Homeostatic Defense & Repair
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-blue-700 mb-2">Cellular Damage Markers</h5>
                  <ul className="text-sm text-blue-600 space-y-1">
                    <li>• AST/ALT/GGT</li>
                    <li>• Lipid Peroxides</li>
                    <li>• oxLDL</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-blue-700 mb-2">Antioxidant Status</h5>
                  <ul className="text-sm text-blue-600 space-y-1">
                    <li>• 8-OHdG (DNA damage)</li>
                    <li>• Total Plasmalogens</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Inflammation */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
              <h4 className="text-lg font-semibold text-orange-800 mb-3 flex items-center">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">I</span>
                Inflammation & Immune Tone
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-orange-700 mb-2">Basic Inflammatory Markers</h5>
                  <ul className="text-sm text-orange-600 space-y-1">
                    <li>• ESR/CRP</li>
                    <li>• IL-6</li>
                    <li>• TNF-alpha</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-orange-700 mb-2">Advanced Inflammatory Profiles</h5>
                  <ul className="text-sm text-orange-600 space-y-1">
                    <li>• Lp-PLA₂ Activity & Myeloperoxidase</li>
                    <li>• TMAO</li>
                    <li>• Vitamin D</li>
                    <li>• Omega-3 Index</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Fibrosis */}
            <div className="bg-gradient-to-r from-cyan-50 to-cyan-100 p-4 rounded-lg border border-cyan-200">
              <h4 className="text-lg font-semibold text-cyan-800 mb-3 flex items-center">
                <span className="bg-cyan-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">F</span>
                Fibrosis / Structural Remodeling
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-cyan-700 mb-2">Fibrotic Markers</h5>
                  <ul className="text-sm text-cyan-600 space-y-1">
                    <li>• TGF-beta</li>
                    <li>• Cystatin C (kidney function)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Timekeeping */}
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
              <h4 className="text-lg font-semibold text-purple-800 mb-3 flex items-center">
                <span className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">T</span>
                Timekeeping & Chronobiology
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-purple-700 mb-2">Circadian Function</h5>
                  <ul className="text-sm text-purple-600 space-y-1">
                    <li>• 24-hour Cortisol Profile</li>
                    <li>• Neurotransmitter Measurements</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-purple-700 mb-2">Biological Age Assessment</h5>
                  <ul className="text-sm text-purple-600 space-y-1">
                    <li>• DNA Methylation (Biological Age)</li>
                    <li>• Heart Rate Variability (HRV)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-2">Clinical Implementation Notes</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Start with basic markers in each domain, then advance to specialized testing based on findings</li>
              <li>• Consider patient-specific risk factors and symptoms when prioritizing test selection</li>
              <li>• Establish baseline values and track trends over time rather than relying on single measurements</li>
              <li>• Integrate results across domains to identify systemic patterns and prioritize interventions</li>
            </ul>
          </div>
        </>
      )
    }
  ];

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow max-w-5xl">
        {/* Header with navigation */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/mentorship-content")}
              className="text-slate-600 hover:text-slate-900 mr-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Mentorship
            </Button>
          </div>
          
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            The SHIFT Framework for Health and Longevity
          </h1>
        </div>
        
        {/* Brief Introduction */}
        <div className="bg-white/70 backdrop-blur-sm border border-slate-200 rounded-xl shadow-sm p-6 mb-8">
          <p className="text-base text-slate-700 leading-relaxed">
            Explore the SHIFT framework - a structured approach to understanding and addressing the cellular mechanisms of aging and longevity. Navigate through the slides below while listening to the accompanying audio lecture.
          </p>
        </div>
        
        {/* Main Content Area */}
        <div className="space-y-8">
          {/* Lecture Slides */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Lecture Presentation</h2>
            <Slideshow slides={lectureSlides} />
          </div>

          {/* Accompanying Audio */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-100">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Audio Narration</h2>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <iframe 
                src="https://share.descript.com/embed/rXudRMlFRaV" 
                width="100%" 
                height="360" 
                frameBorder="0" 
                allowFullScreen
                className="w-full"
                title="SHIFT Framework Health Longevity Audio Lecture"
              ></iframe>
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center italic">
              Listen to the audio while navigating through the slides for the complete learning experience
            </p>
          </div>
          
          {/* Simple Navigation Footer */}
          <div className="border-t border-slate-200 pt-8 mt-12">
            <div className="flex justify-between items-center">
              <Button 
                variant="outline"
                onClick={() => navigate("/mentorship-content")}
                className="text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Return to Course List
              </Button>
              
              <div className="text-sm text-slate-500">
                Part of the NGM Mentorship Program
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
      </div>
    </MentorshipAccessGate>
  );
}

export default ShiftFrameworkHealthLongevity;